import type { Extension } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import type { BasicSetupOptions } from '@uiw/react-codemirror';
import CodeMirrorEditor from 'components/editors/CodeMirrorEditor';
import { type FC, useMemo } from 'react';

const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, monospace';
const SANS_FONT = 'Jost, sans-serif';

// Minimalist setup (no line numbers / active-line highlight) so plain-text fields keep the
// lightweight feel of the previous textarea; in-editor search (Cmd/Ctrl+F) stays enabled.
const PLAIN_BASIC_SETUP: BasicSetupOptions = {
  lineNumbers: false,
  highlightActiveLine: false,
  highlightActiveLineGutter: false,
};

interface CodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minRows?: number;
  maxRows?: number;
  error?: boolean;
  fontSize?: number;
  ariaLabel?: string;
  monospace?: boolean;
}

/**
 * Borderless multiline editor on a tonal surface (design system: no 1px borders), built on
 * the shared {@link CodeMirrorEditor}. Used for the system prompt and chat-turn editing.
 */
const CodeEditor: FC<CodeEditorProps> = ({
  value,
  onChange,
  placeholder,
  minRows = 4,
  maxRows,
  error = false,
  fontSize = 12.5,
  ariaLabel,
  monospace = true,
}) => {
  const extensions = useMemo<Extension[]>(
    () => [
      EditorView.theme({ '.cm-content': { fontFamily: monospace ? MONO_FONT : SANS_FONT, fontSize: `${fontSize}px` } }),
    ],
    [monospace, fontSize]
  );

  // Approximate the old `rows` sizing: line-height ≈ 1.6em plus the content's vertical padding.
  const rowPx = fontSize * 1.6;
  const minHeight = Math.round(minRows * rowPx + 16);
  const maxHeight = maxRows ? Math.round(maxRows * rowPx + 16) : undefined;

  return (
    <CodeMirrorEditor
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      error={error}
      ariaLabel={ariaLabel}
      extensions={extensions}
      basicSetup={PLAIN_BASIC_SETUP}
      minHeight={minHeight}
      maxHeight={maxHeight}
    />
  );
};

export default CodeEditor;
